// src/pages/teams/Teams.jsx
'use client';

import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useDeferredValue,
} from 'react';
import { Link as RouterLink, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';
import toast from 'react-hot-toast';

// Services
import { createInvite, buildInviteUrl } from '@/services/inviteService';
import { unarchiveTeam, getTeamUsage, archiveTeamSafely, deleteTeamIfEmpty } from '@/services/teamService';

// Firebase
import { db, app } from '@/firebase/firebase';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Context & Hooks
import { useAuth } from '@/context/AuthContext';
import { useTeam } from '@/context/TeamContext';
import useEntitlements from '@/hooks/useEntitlements';
import { usePlatformClaims } from '@/hooks/usePlatformClaims';
import useChats from '@/hooks/useChats';
import { teamAccessFromDoc, canInviteStaff } from '@/constants/roles';

// UI Components
import { Avatar } from '@/components/avatar';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '@/components/dropdown';
import { Skeleton } from '@/components/skeleton';
import Modal from '@/components/Modal';
import SendInviteModal from '@/components/invite/SendInviteModal';
import InviteSuccessModal from '@/components/invite/InviteSuccessModal';

// Icons
import {
  EllipsisHorizontalIcon,
  MapPinIcon,
  PlusIcon,
  PencilSquareIcon,
  ArrowTopRightOnSquareIcon,
  LinkIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  MagnifyingGlassIcon,
  TrophyIcon,
  SparklesIcon,
  ArchiveBoxIcon,
  TrashIcon,
  ArrowPathIcon,
  LockClosedIcon,
  CalendarDaysIcon,
  UserPlusIcon,
  UserGroupIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import { ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

/* ------------------------------ Visual Constants ------------------------------ */
const HEAD_FONT = "'Barlow Condensed', sans-serif";
const LIME = '#b5f03d';
const LIME_DARK = '#94cc28';
const PITCH = '#0d1f14';

const FADE_IN = {
  hidden: { opacity: 0, y: 10 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.05, duration: 0.3, ease: 'easeOut' },
  }),
  exit: { opacity: 0, scale: 0.95 }
};

function SectionLabel({ children, className = '' }) {
  return (
    <p
      className={`text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f] ${className}`}
      style={{ fontFamily: HEAD_FONT }}
    >
      {children}
    </p>
  );
}

function PrimaryButton({ children, disabled, onClick, className = '', type = 'button', as: As = 'button', ...rest }) {
  return (
    <As
      type={As === 'button' ? type : undefined}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 min-h-[44px] uppercase transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
      style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 14, letterSpacing: '0.6px' }}
      {...rest}
    >
      {children}
    </As>
  );
}

function GhostButton({ children, onClick, disabled, className = '', type = 'button', as: As = 'button', ...rest }) {
  return (
    <As
      type={As === 'button' ? type : undefined}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-50 hover:border-[#b5f03d]/40 px-3.5 py-2 text-[13px] font-semibold text-[#0d1f14] transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
      {...rest}
    >
      {children}
    </As>
  );
}

function DangerButton({ children, onClick, disabled, className = '', type = 'button' }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 hover:bg-rose-100 hover:border-rose-300 px-3.5 py-2 text-[13px] font-semibold text-rose-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${className}`}
    >
      {children}
    </button>
  );
}

/* ---------------------------- Helpers ---------------------------- */
const isArchivedFlag = (t) => t?.isArchived === undefined ? !!t?.archived : !!t?.isArchived;

function computeAddressLines(fullAddress, placeName) {
  const out = { street: '', cityLine: '' };
  if (!fullAddress) return out;
  const parts = fullAddress.split(',').map((s) => s.trim()).filter(Boolean);
  if (!parts.length) { out.street = fullAddress; return out; }
  let idx = 0;
  if (placeName && parts[0].toLowerCase() === placeName.toLowerCase()) idx = 1;
  out.street = parts[idx] || '';
  out.cityLine = parts.slice(idx + 1, idx + 3).join(', '); 
  return out;
}

// Human-friendly "next session" label: "Today 3:00 PM", "Tomorrow…", "Sat 3:00 PM", or "Jan 15"
function formatSessionLabel(session) {
  if (!session) return null;
  const raw = session.date;
  const d = raw?.toDate?.() || (raw instanceof Date ? raw : raw ? new Date(raw) : null);
  if (!d || Number.isNaN(d.getTime())) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const that  = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayDiff = Math.round((that - today) / (1000 * 60 * 60 * 24));
  const timeStr = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (dayDiff === 0) return `Today · ${timeStr}`;
  if (dayDiff === 1) return `Tomorrow · ${timeStr}`;
  if (dayDiff > 1 && dayDiff < 7) {
    return `${d.toLocaleDateString(undefined, { weekday: 'short' })} · ${timeStr}`;
  }
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · ${timeStr}`;
}

// NOTE: the previous `getRoleForUserOnTeam` + `CAN_INVITE_ROLES` pair
// treated any staff member (coach / manager / assistant) as "can invite".
// That was more permissive than the server gate — functions/modules/invites
// requires canInviteStaff(access), i.e. TEAM_OWNER or above, and would reject
// the call anyway. We now resolve canonical ACCESS.* via teamAccessFromDoc()
// (sync; reads only the already-loaded team doc + JWT claims) and hand the
// result through canInviteStaff, keeping UI and server in agreement.

/* ---------------------------- Sub-Components ---------------------------- */

function StatBadge({ icon: Icon, value, label, colorClass = "text-zinc-600 bg-zinc-100" }) {
  if (!value) return null;
  return (
    <div className={clsx("inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border border-transparent", colorClass)}>
      <Icon className="h-3.5 w-3.5" />
      <span>{value}</span>
      <span className="opacity-70">{label}</span>
    </div>
  );
}

function TeamStatsDisplay({ team }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(team?.stats || null);
  
  useEffect(() => {
    if (team?.stats) { setStats(team.stats); return; }
    if (!team?.id) return;
    const q = query(collection(db, 'team_stats'), where('__name__', '==', team.id));
    const unsub = onSnapshot(q, (s) => { if(s.docs[0]?.exists()) setStats(s.docs[0].data()); });
    return () => unsub();
  }, [team?.id]);

  if (!stats) return null;
  
  const matchPct = Math.round(stats.matchPct || 0);
  const rating = Number(stats.avgRating || 0); 
  const ratingCount = Number(stats.ratingCount || 0);
  
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {rating > 0 && (
        <StatBadge 
          icon={StarIconSolid} 
          value={rating.toFixed(1)} 
          label={`(${ratingCount})`} 
          colorClass="text-amber-700 bg-amber-50 border-amber-100"
        />
      )}
      {matchPct > 0 && (
        <StatBadge
          icon={SparklesIcon}
          value={`${matchPct}%`}
          label={t('teams.card.matchRate')}
          colorClass="text-[#3d6010] bg-[#b5f03d]/15 border-[#b5f03d]/40"
        />
      )}
    </div>
  );
}

/* --------------------------- Invite Options Modal ---------------------------- */
// Mobile-friendly action sheet that replaces the old chevron dropdown on every
// team card. Each row is full-width with a 56px+ tap target so it works on
// touch devices without a hover state.
function InviteOptionsModal({
  team,
  onClose,
  onAddPlayers,
  onInviteParents,
  onInviteStaffEmail,
  onCopyStaffLink,
  onShareViaSMS,
}) {
  const { t } = useTranslation();
  const open = !!team;

  const fire = (fn) => () => {
    onClose?.();
    // Defer to next tick so the modal close animation doesn't race child modals
    setTimeout(() => fn?.(team), 0);
  };

  const options = [
    { key: 'players',    icon: UserPlusIcon,                 label: t('teams.inviteOpts.players.label'),    sub: t('teams.inviteOpts.players.sub'),    run: fire(onAddPlayers) },
    { key: 'parents',    icon: UserGroupIcon,                label: t('teams.inviteOpts.parents.label'),    sub: t('teams.inviteOpts.parents.sub'),    run: fire(onInviteParents) },
    { key: 'staffEmail', icon: AcademicCapIcon,              label: t('teams.inviteOpts.staffEmail.label'), sub: t('teams.inviteOpts.staffEmail.sub'), run: fire(onInviteStaffEmail) },
    { key: 'copyLink',   icon: ClipboardDocumentCheckIcon,   label: t('teams.inviteOpts.copyLink.label'),   sub: t('teams.inviteOpts.copyLink.sub'),   run: fire(onCopyStaffLink) },
    { key: 'sms',        icon: ChatBubbleLeftRightIcon,      label: t('teams.inviteOpts.sms.label'),        sub: t('teams.inviteOpts.sms.sub'),        run: fire(onShareViaSMS) },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={team ? t('teams.inviteModal.titleFor', { name: team.name || 'team' }) : t('teams.inviteModal.title')}
    >
      <ul className="space-y-2 -mt-1">
        {options.map(({ key, icon: Icon, label, sub, run }) => (
          <li key={key}>
            <button
              type="button"
              onClick={run}
              className="w-full flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-3.5 py-3 text-left min-h-[56px] hover:border-[#b5f03d]/60 hover:bg-[#b5f03d]/5 active:bg-[#b5f03d]/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b5f03d]"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-[#5a8a1f]">
                <Icon className="h-5 w-5" />
              </span>
              <span className="flex-1 min-w-0">
                <span
                  className="block text-sm font-bold uppercase tracking-wide text-[#0d1f14]"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.4px' }}
                >
                  {label}
                </span>
                <span className="block text-xs text-zinc-500 mt-0.5 truncate">{sub}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </Modal>
  );
}

/* --------------------------- Team Card Component ----------------------------- */
function TeamCard({
  team,
  i,
  onArchive,
  onUnarchive,
  onDelete,
  onOpenInviteOptions,
  onLeave,
  inviting,
  canInvite,
  unreadCount = 0,
  nextSession = null,
}) {
  const { t } = useTranslation();
  const { firebaseUser, user: userProfile } = useAuth(); // Need auth here to check owner
  const { setCurrentTeam } = useTeam();
  const archived = isArchivedFlag(team);

  const uid = firebaseUser?.uid || userProfile?.uid;
  const isOwner = team.ownerId === uid;

  const preselectTeam = () => setCurrentTeam?.({ id: team.id, name: team.name, logoUrl: team.logoUrl });

  const placeName = team?.location?.name || '';
  const { street, cityLine } = computeAddressLines(team?.address || '', placeName);

  const handleAction = (fn) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    preselectTeam();
    fn?.();
  };

  const openInviteHandler = handleAction(() => {
    if (!canInvite) { toast.error(t('teams.toasts.noPermission')); return; }
    onOpenInviteOptions?.(team);
  });

  return (
    <motion.li
      layout
      custom={i}
      variants={FADE_IN}
      initial="hidden"
      animate="show"
      exit="exit"
      className={clsx(
        'group relative flex flex-col rounded-2xl bg-white border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] transition-all duration-300 hover:shadow-[0_8px_24px_-8px_rgba(13,31,20,0.18)] hover:border-[#b5f03d]/40 hover:-translate-y-0.5',
        archived && 'opacity-60 grayscale'
      )}
    >
      <div className={clsx("h-1.5 w-full rounded-t-2xl", archived ? "bg-zinc-200" : "bg-gradient-to-r from-[#b5f03d] to-[#94cc28]")} />

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between">
          <div className="flex gap-4">
            <Avatar
              src={team.logoUrl}
              initials={(team.name || 'T').charAt(0)}
              square
              className="!h-14 !w-14 rounded-xl shadow-sm ring-1 ring-[#0d1f14]/5"
            />
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <RouterLink
                  to={`/teams/${team.id}`}
                  onClick={preselectTeam}
                  className="block truncate text-lg font-bold text-[#0d1f14] hover:text-[#5a8a1f] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b5f03d] rounded-sm"
                  style={{ fontFamily: HEAD_FONT, letterSpacing: '0.2px' }}
                >
                  {team.name || t('teams.card.unnamed')}
                </RouterLink>
                {unreadCount > 0 && (
                  <RouterLink
                    to={`/messages?teamId=${team.id}`}
                    onClick={(e) => { e.stopPropagation(); preselectTeam(); }}
                    aria-label={`${unreadCount} unread ${unreadCount === 1 ? 'message' : 'messages'}`}
                    title={`${unreadCount} unread ${unreadCount === 1 ? 'message' : 'messages'}`}
                    className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#b5f03d]/15 px-2 py-0.5 text-[10px] font-bold text-[#3d6010] ring-1 ring-inset ring-[#b5f03d]/40 hover:bg-[#b5f03d]/25 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b5f03d]"
                  >
                    <ChatBubbleLeftEllipsisIcon aria-hidden className="h-3 w-3" />
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </RouterLink>
                )}
              </div>
              {(() => {
                const facets = [team.ageGroup, team.skillLevel, team.gender].filter(Boolean);
                if (facets.length === 0) {
                  return (
                    <RouterLink
                      to={`/teams/${team.id}/edit`}
                      onClick={preselectTeam}
                      className="mt-1 inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-[#5a8a1f] italic"
                    >
                      <PencilSquareIcon className="h-3 w-3" />
                      {t('teams.card.addDetails')}
                    </RouterLink>
                  );
                }
                return (
                  <div className="flex flex-wrap items-center gap-x-2 text-xs text-zinc-500 mt-1">
                    {facets.map((f, idx) => (
                      <React.Fragment key={idx}>
                        {idx > 0 && <span aria-hidden>•</span>}
                        <span className={idx === 0 ? "font-medium text-zinc-700" : undefined}>{f}</span>
                      </React.Fragment>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>

          <Dropdown>
            <DropdownButton plain className="!p-1 text-zinc-400 hover:text-[#0d1f14]">
              <EllipsisHorizontalIcon className="h-6 w-6" />
            </DropdownButton>
            <DropdownMenu anchor="bottom end" className="z-40 w-48 right-0 origin-top-right">
              <DropdownItem as={RouterLink} to={`/teams/${team.id}/edit`} onClick={preselectTeam}>
                <PencilSquareIcon className="h-4 w-4"/> <DropdownLabel>{t('teams.menu.edit')}</DropdownLabel>
              </DropdownItem>
              <DropdownItem onClick={handleAction(() => archived ? onUnarchive(team) : onArchive(team))}>
                <ArchiveBoxIcon className="h-4 w-4"/>
                <DropdownLabel>{archived ? t('teams.menu.restore') : t('teams.menu.archive')}</DropdownLabel>
              </DropdownItem>

              {/* Delete is only useful for accidental teams the owner just
                  created. The handler in the parent calls deleteTeamIfEmpty
                  which checks for any data (players/events/scrimmages/extra
                  members) and refuses with a clear "use Archive instead"
                  toast if the team isn't empty. We still show the option to
                  every owner so they can try — the safety check lives at
                  the service layer, not the UI. */}
              {isOwner && (
                <DropdownItem onClick={handleAction(() => onDelete?.(team))}>
                  <TrashIcon className="h-4 w-4 text-red-500"/>
                  <DropdownLabel className="text-red-600">{t('teams.menu.delete')}</DropdownLabel>
                </DropdownItem>
              )}

              {!isOwner && (
                <DropdownItem onClick={handleAction(() => onLeave(team))}>
                  <ArrowTopRightOnSquareIcon className="h-4 w-4"/> <DropdownLabel>{t('teams.menu.leave')}</DropdownLabel>
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        </div>

        <div className="mt-4 space-y-3 flex-1">
          {(placeName || street) ? (
            <div className="flex items-start gap-2 text-xs text-zinc-500">
              <MapPinIcon className="h-4 w-4 shrink-0 mt-0.5 text-zinc-400" />
              <span className="line-clamp-2">
                {placeName && <strong className="font-medium text-zinc-700">{placeName}</strong>}
                {placeName && street && ', '}
                {street}
                {cityLine && <span className="block opacity-75">{cityLine}</span>}
              </span>
            </div>
          ) : (
            isOwner ? (
              <RouterLink
                to={`/teams/${team.id}/edit`}
                onClick={preselectTeam}
                className="inline-flex items-center gap-2 text-xs text-zinc-400 hover:text-[#5a8a1f] italic transition-colors"
              >
                <MapPinIcon className="h-4 w-4 shrink-0" /> {t('teams.card.addLocation')}
              </RouterLink>
            ) : (
              <div className="flex items-center gap-2 text-xs text-zinc-400 italic">
                <MapPinIcon className="h-4 w-4 shrink-0" /> {t('teams.card.noLocation')}
              </div>
            )
          )}

          {(() => {
            const label = formatSessionLabel(nextSession);
            if (!label) return null;
            return (
              <div className="flex items-center gap-2 text-xs text-zinc-600">
                <CalendarDaysIcon className="h-4 w-4 shrink-0 text-zinc-400" aria-hidden />
                <span className="truncate">
                  <span className="font-medium text-zinc-800">{nextSession?.type || t('teams.card.session')}</span>
                  <span className="text-zinc-500"> · {label}</span>
                </span>
              </div>
            );
          })()}

          <TeamStatsDisplay team={team} />
        </div>
      </div>

      {/*
        Footer action — single high-visibility "Invite" button.
        Tap opens InviteOptionsModal listing every invite path (players, parents,
        staff email, copy staff link, SMS share). Replaces a former chevron
        dropdown that hid most invite paths from coaches at a glance.
      */}
      <div className="border-t border-zinc-200 bg-[#f7faf5] rounded-b-2xl">
        <button
          type="button"
          onClick={openInviteHandler}
          disabled={!canInvite || inviting}
          aria-label={canInvite ? `Invite to ${team.name}` : 'Invite (not permitted)'}
          className="w-full flex items-center justify-center gap-2 py-3.5 text-sm font-bold uppercase text-[#0d1f14] bg-[#b5f03d] hover:bg-[#94cc28] rounded-b-2xl disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#94cc28] transition-colors"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
        >
          <UserPlusIcon className="h-4 w-4" />
          {inviting ? t('teams.card.working') : t('teams.card.invite')}
        </button>
      </div>
    </motion.li>
  );
}

/* =============================== MAIN PAGE ================================ */
export default function TeamsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { firebaseUser, userProfile, loading: authLoading } = useAuth();
  const { claims } = usePlatformClaims();
  const { isPro, loadingTeams, canAddTeam } = useTeam();
  const { loading: entitlementsLoading } = useEntitlements(firebaseUser?.uid);
  
  // URL Params
  const [params, setParams] = useSearchParams();
  const initialQ = params.get('q') ?? '';
  const initialSort = params.get('sort') ?? 'updated';
  const [qText, setQText] = useState(initialQ);
  const [sortBy, setSortBy] = useState(initialSort);
  const deferredQ = useDeferredValue(qText);

  // State
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  // unreadByTeam is now a useMemo derived from chatsForUser — see the
  // useChats block below. Keeping it as useState would loop infinitely
  // because chatsForUser is a new array reference on every render of
  // useChats, so any useEffect with [chatsForUser] in deps + setState
  // re-fires on every render → re-render → effect → setState → ...
  const [nextSessionByTeam, setNextSessionByTeam] = useState({});
  const [teamToArchive, setTeamToArchive] = useState(null);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [reason, setReason] = useState('');
  
  const [invitingTeamId, setInvitingTeamId] = useState(null);
  const [sendInviteFor, setSendInviteFor] = useState(null);
  const [sendChannel, setSendChannel] = useState(null);
  const [inviteOptionsFor, setInviteOptionsFor] = useState(null); // team being invited to

  // Success Modal
  const [successModalData, setSuccessModalData] = useState(null);

  // Invite label + leave confirm modals (replace window.prompt / window.confirm)
  const [labelModal, setLabelModal] = useState(null); // { team, label }
  const [leaveModal, setLeaveModal] = useState(null); // team

  const searchRef = useRef(null);
  const uid = firebaseUser?.uid || userProfile?.uid;

  const handleCreateTeamClick = (e) => {
    // Phase 1 (per docs/pricing-strategy.md): no plan gates on team creation.
    // Founding clubs are manually billed; tier enforcement ships in Phase 3.
    // if (!canAddTeam) {
    //   e.preventDefault();
    //   toast('Upgrade to Pro to manage multiple teams', { icon: '🔒' });
    //   navigate('/billing/upgrade');
    // }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(params);
    qText ? p.set('q', qText) : p.delete('q');
    sortBy !== 'updated' ? p.set('sort', sortBy) : p.delete('sort');
    setParams(p, { replace: true });
  }, [qText, sortBy]);

  useEffect(() => {
    if (authLoading) { setLoading(true); return; }
    if (!uid) { setTeams([]); setLoading(false); return; }

    setLoading(true);
    const teamsCol = collection(db, 'teams');
    const queries = [
      query(teamsCol, where('ownerId', '==', uid)),
      query(teamsCol, where('coachId', '==', uid)),
      query(teamsCol, where('staffIds', 'array-contains', uid))
    ];

    // One Map per query, plus a per-query "first snapshot received" flag.
    // We only set loading=false after ALL queries have produced at least one
    // snapshot, so we never show a half-populated list.
    const results = [new Map(), new Map(), new Map()];
    const initialLoaded = [false, false, false];
    const unsubs = [];

    const mergeAndPush = () => {
      const merged = new Map([...results[0], ...results[1], ...results[2]]);
      setTeams(Array.from(merged.values()));
      if (initialLoaded.every(Boolean)) setLoading(false);
    };

    queries.forEach((q, idx) => {
      unsubs.push(onSnapshot(
        q,
        (snap) => {
          results[idx] = new Map(snap.docs.map(d => [d.id, { id: d.id, ...d.data() }]));
          initialLoaded[idx] = true;
          mergeAndPush();
        },
        (err) => {
          // Permission errors on one scope shouldn't hang the spinner forever.
          console.error(`Teams query ${idx} failed:`, err);
          initialLoaded[idx] = true;
          mergeAndPush();
        }
      ));
    });

    return () => unsubs.forEach(u => u());
  }, [uid, authLoading]);

  // Stable key so the unread / next-session listeners only re-subscribe when
  // the SET of team ids actually changes (not on every team doc update).
  const teamIdsKey = useMemo(
    () => teams.map(t => t.id).filter(Boolean).sort().join(','),
    [teams]
  );

  // Per-team unread chat count.
  //
  // Old approach (now removed): subscribe to chats with
  //   where('teams', 'array-contains-any', chunk)
  // That query has no participantIds filter, so Firestore's static
  // rule analyzer rejected it (chats rule requires
  // "request.auth.uid in resource.data.participantIds" — a query
  // that doesn't filter on participantIds can't be proven safe).
  // Result: "Unread chats listener failed: Missing or insufficient
  // permissions" in the console, badges never populated.
  //
  // New approach: piggyback on useChats (which does the rule-safe
  // query — where('participantIds', 'array-contains', uid)) and
  // aggregate isUnread by teamId. Single subscription, no permission
  // errors, inherits v2 read-receipt semantics.
  //
  // IMPORTANT: this MUST be a useMemo (not useEffect + setState).
  // useChats returns a fresh `chats` array reference on every render
  // (because of `chats.map(...)` in its decorator). If we put it in
  // a useEffect dep array and called setUnreadByTeam each time, the
  // setState would trigger a re-render → new chatsForUser ref →
  // effect runs again → infinite loop. The page locks up so badly
  // that even sidebar nav clicks stop responding.
  const { chats: chatsForUser } = useChats(uid);
  const unreadByTeam = useMemo(() => {
    if (!teamIdsKey) return {};
    const teamIds = teamIdsKey.split(',');
    const counts = Object.fromEntries(teamIds.map((tid) => [tid, 0]));
    chatsForUser.forEach((c) => {
      if (!c.isUnread) return;
      const tid = c.teamId;
      if (tid && counts[tid] != null) counts[tid] += 1;
    });
    return counts;
  }, [teamIdsKey, chatsForUser]);

  // Per-team next upcoming session. Query by teamId (chunked), sort client-side
  // so we don't need an extra composite index.
  useEffect(() => {
    const teamIds = teamIdsKey ? teamIdsKey.split(',') : [];
    if (!teamIds.length) { setNextSessionByTeam({}); return; }

    const chunks = [];
    for (let i = 0; i < teamIds.length; i += 30) chunks.push(teamIds.slice(i, i + 30));

    const perChunkNext = chunks.map(() => new Map());
    const publish = () => {
      const merged = {};
      perChunkNext.forEach(m => m.forEach((v, k) => { merged[k] = v; }));
      setNextSessionByTeam(merged);
    };

    const unsubs = chunks.map((chunk, ci) => {
      const qy = query(collection(db, 'sessions'), where('teamId', 'in', chunk));
      return onSnapshot(
        qy,
        (snap) => {
          const nowMs = Date.now();
          const nextByTeam = new Map(chunk.map(tid => [tid, null]));
          snap.docs.forEach(d => {
            const data = { id: d.id, ...d.data() };
            const tid = data.teamId;
            if (!tid || !nextByTeam.has(tid)) return;
            const ms = data.date?.toMillis?.()
              ?? (data.date instanceof Date ? data.date.getTime()
              :   data.date ? new Date(data.date).getTime() : 0);
            if (!ms || ms < nowMs) return;
            const current = nextByTeam.get(tid);
            const curMs = current?.date?.toMillis?.()
              ?? (current?.date instanceof Date ? current.date.getTime()
              :   current?.date ? new Date(current.date).getTime() : Infinity);
            if (!current || ms < curMs) nextByTeam.set(tid, data);
          });
          perChunkNext[ci] = nextByTeam;
          publish();
        },
        (err) => {
          console.error('Sessions listener failed:', err);
          perChunkNext[ci] = new Map(chunk.map(tid => [tid, null]));
          publish();
        }
      );
    });
    return () => unsubs.forEach(u => u && u());
  }, [teamIdsKey]);

  const groupedTeams = useMemo(() => {
    let base = teams.filter(t => !isArchivedFlag(t));
    if (deferredQ) {
      const lc = deferredQ.toLowerCase();
      base = base.filter(t => (t.name || '').toLowerCase().includes(lc));
    }

    const owned = [], managed = [];
    base.forEach(t => {
      const isOwner = t.ownerId === uid || t.coachId === uid;
      isOwner ? owned.push(t) : managed.push(t);
    });

    const sortFn = sortBy === 'alpha' 
      ? (a,b) => (a.name || '').localeCompare(b.name || '')
      : (a,b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0);

    return { owned: owned.sort(sortFn), managed: managed.sort(sortFn) };
  }, [teams, uid, deferredQ, sortBy]);

  useEffect(() => {
    if (!teamToArchive?.id) { setUsage(null); setReason(''); return; }
    setUsageLoading(true);
    getTeamUsage(teamToArchive.id)
      .then(setUsage)
      .catch(() => setUsage(null))
      .finally(() => setUsageLoading(false));
  }, [teamToArchive?.id]);

  const confirmArchive = async () => {
    if (!teamToArchive) return;
    const hasLive = (usage?.scrimmagesOpen || 0) > 0 || (usage?.scrimmagesConfirmed || 0) > 0;
    if (hasLive) { toast.error(t('teams.toasts.cannotArchiveActive')); return; }

    const toastId = toast.loading(t('teams.toasts.archiving'));
    try {
      await archiveTeamSafely(teamToArchive.id, { reason: reason?.trim() });
      toast.success(t('teams.toasts.archived'), { id: toastId });
      setTeamToArchive(null);
    } catch {
      toast.error(t('teams.toasts.archiveFailed'), { id: toastId });
    }
  };

  const handleUnarchive = async (team) => {
    const toastId = toast.loading(t('teams.toasts.restoring'));
    try {
      await unarchiveTeam(team.id);
      toast.success(t('teams.toasts.restored'), { id: toastId });
    } catch { toast.error(t('teams.toasts.restoreFailed'), { id: toastId }); }
  };

  // Delete = the "I just made this by accident" escape hatch. Owner-only,
  // requires confirmation, and only succeeds for empty teams. Anything
  // populated bounces with a clear "use Archive instead" toast — full
  // cascade-delete of populated teams is a Cloud Function task we'll
  // wire up later.
  const handleDelete = async (team) => {
    if (!team?.id) return;
    const ok = window.confirm(t('teams.toasts.deleteConfirm', { name: team.name || 'this team' }));
    if (!ok) return;

    const toastId = toast.loading(t('teams.toasts.deleting'));
    try {
      const result = await deleteTeamIfEmpty(team.id, { uid });
      if (result.ok) {
        toast.success(t('teams.toasts.deleted'), { id: toastId });
        return;
      }
      if (result.reason === 'has_data') {
        const c = result.counts || {};
        const parts = [];
        if (c.players) parts.push(t('teams.units.player', { count: c.players }));
        if (c.events) parts.push(t('teams.units.event', { count: c.events }));
        if (c.scrimmages) parts.push(t('teams.units.scrimmage', { count: c.scrimmages }));
        if (c.extraMembers) parts.push(t('teams.units.member', { count: c.extraMembers }));
        toast.error(
          t('teams.toasts.hasData', { items: parts.join(', ') || t('teams.units.data') }),
          { id: toastId, duration: 6500 }
        );
        return;
      }
      if (result.reason === 'not_owner') {
        toast.error(t('teams.toasts.notOwner'), { id: toastId });
        return;
      }
      toast.error(t('teams.toasts.deleteFailed'), { id: toastId });
    } catch (err) {
      console.error('[Teams] delete failed', err);
      toast.error(t('teams.toasts.deleteFailed'), { id: toastId });
    }
  };

  const openSendInvite = (team, { channel } = {}) => {
    setInvitingTeamId(team.id);
    setSendChannel(channel);
    setSendInviteFor(team);
    setTimeout(() => setInvitingTeamId(null), 100);
  };

  // Open the labeled-invite modal (replaces window.prompt)
  const quickInviteLink = (team) => {
    if (!uid) return;
    setLabelModal({ team, label: '' });
  };

  // Called after the user submits a label in the modal
  const createLabeledInviteLink = async () => {
    if (!labelModal?.team || !uid) return;
    const { team, label } = labelModal;
    const trimmed = (label || '').trim();
    if (!trimmed) {
      toast.error(t('teams.toasts.enterLabel'));
      return;
    }
    setLabelModal(null);
    setInvitingTeamId(team.id);
    const toastId = toast.loading(t('teams.toasts.creatingLink'));
    try {
      const { code } = await createInvite({
        teamId: team.id,
        createdBy: uid,
        audience: 'staff',
        preset: 'manager',
        maxUses: 1,
        expiresInDays: 14,
        metadata: {
          label: trimmed,
          createdVia: 'quick-action'
        }
      });
      if (code) {
        toast.dismiss(toastId);
        setSuccessModalData({
          open: true,
          url: buildInviteUrl(code, team.id)
        });
      }
    } catch (e) {
      toast.error(e.message || 'Failed to create invite', { id: toastId });
    } finally {
      setInvitingTeamId(null);
    }
  };

  // One-click roster invite link for player or parent audiences.
  // Skips the label prompt — coaches adding rostered kids want speed.
  const createRosterLink = async (team, audience) => {
    if (!uid || !team) return;
    const aud = audience === 'player' ? 'player' : 'parent';
    const friendly = aud === 'player' ? t('teams.rolePlayer') : t('teams.roleParent');
    setInvitingTeamId(team.id);
    const toastId = toast.loading(t('teams.toasts.creatingInvite', { role: friendly }));
    try {
      const { code } = await createInvite({
        teamId: team.id,
        createdBy: uid,
        audience: aud,
        preset: aud,
        maxUses: 25,                 // shareable for roster growth
        expiresInDays: 30,
        metadata: {
          label: aud === 'player' ? 'Player signup link' : 'Parent signup link',
          createdVia: 'team-card-roster'
        }
      });
      if (code) {
        toast.dismiss(toastId);
        setSuccessModalData({
          open: true,
          url: buildInviteUrl(code, team.id)
        });
      }
    } catch (e) {
      toast.error(e.message || 'Failed to create invite', { id: toastId });
    } finally {
      setInvitingTeamId(null);
    }
  };

  const handleShareViaSMS = async (team) => {
    if (!uid) return;
    setInvitingTeamId(team.id);
    const toastId = toast.loading(t('teams.toasts.preparingSms'));
    try {
      const { code } = await createInvite({
        teamId: team.id, 
        createdBy: uid, 
        audience: 'staff', 
        preset: 'manager',
        maxUses: 1, 
        expiresInDays: 14,
        metadata: {
          label: 'SMS Share', // Added for consistency
          createdVia: 'sms-share'
        }
      });
      if(code) {
        const url = buildInviteUrl(code, team.id);
        window.location.href = `sms:?&body=${encodeURIComponent(`Join ${team.name} on Scrimly: ${url}`)}`;
        toast.dismiss(toastId);
      }
    } catch { toast.error(t('teams.toasts.failed'), { id: toastId }); }
    finally { setInvitingTeamId(null); }
  };

  // Open leave-team confirmation modal (replaces window.confirm)
  const leaveTeam = (team) => {
    setLeaveModal(team);
  };

  const confirmLeaveTeam = async () => {
    const team = leaveModal;
    if (!team) return;
    setLeaveModal(null);
    const toastId = toast.loading(t('teams.toasts.leaving'));
    try {
      const fn = httpsCallable(getFunctions(app), 'leaveTeam');
      await fn({ teamId: team.id });
      toast.success(t('teams.toasts.left'), { id: toastId });
    } catch { toast.error(t('teams.toasts.leaveFailed'), { id: toastId }); }
  };

  const renderList = (list) => (
    <div
      className="grid gap-6 justify-start"
      style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 380px))' }}
    >
      <AnimatePresence>
        {list.map((team, i) => (
          <TeamCard
            key={team.id}
            team={team}
            i={i}
            onArchive={setTeamToArchive}
            onUnarchive={handleUnarchive}
            onDelete={handleDelete}
            onOpenInviteOptions={setInviteOptionsFor}
            onLeave={leaveTeam}
            inviting={invitingTeamId === team.id}
            canInvite={canInviteStaff(teamAccessFromDoc({ uid, team, claims }))}
            unreadCount={unreadByTeam[team.id] || 0}
            nextSession={nextSessionByTeam[team.id] || null}
          />
        ))}
      </AnimatePresence>
    </div>
  );

  const totalTeams = groupedTeams.owned.length + groupedTeams.managed.length;

  return (
    <div className="bg-[#f7faf5] min-h-screen">
      <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8 overflow-x-hidden">

        {/* Header */}
        <div className="relative z-10 mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <SectionLabel>{t('teams.myRoster')}</SectionLabel>
            <div className="flex items-center gap-3 mt-1">
              <h1
                className="text-3xl sm:text-4xl text-[#0d1f14] uppercase"
                style={{ fontFamily: HEAD_FONT, fontWeight: 800, letterSpacing: '0.5px' }}
              >
                {t('teams.pageTitle')}
              </h1>
              {!loading && totalTeams > 0 && (
                <span
                  aria-label={`${totalTeams} teams`}
                  className="inline-flex items-center justify-center rounded-full bg-[#b5f03d]/15 text-[#3d6010] px-2.5 py-0.5 text-xs font-bold ring-1 ring-[#b5f03d]/40"
                >
                  {totalTeams}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-zinc-600">
               {t('teams.pageDescription')}
            </p>
          </div>
          <div className="w-full sm:w-auto min-w-0 relative z-10">
            <PrimaryButton
                onClick={handleCreateTeamClick}
                as={canAddTeam ? RouterLink : 'button'}
                to={canAddTeam ? "/teams/create" : undefined}
                className="shadow-sm w-full sm:w-auto"
            >
                {canAddTeam ? <PlusIcon className="size-4" /> : <LockClosedIcon className="size-4" />}
                {t('teams.createNew')}
            </PrimaryButton>
          </div>
        </div>

        {/* Pro Banner */}
        {/* Phase 1 (per docs/pricing-strategy.md): no in-app upgrade CTAs until founding clubs validate tiers */}
        {/* {!authLoading && !entitlementsLoading && !isPro && <ProUpsellBanner />} */}

        {/* Sticky Toolbar */}
        <div
          role="search"
          className="sticky top-0 z-30 isolate mb-6 rounded-xl border border-zinc-200 bg-white/90 p-2 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] backdrop-blur-md"
        >
          <div className="flex flex-col sm:flex-row gap-2 items-center">
            <div className="relative flex-1 w-full">
              <label htmlFor="teams-search" className="sr-only">{t('teams.searchPlaceholder')}</label>
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400 pointer-events-none" aria-hidden/>
              <input
                id="teams-search"
                ref={searchRef}
                type="search"
                value={qText}
                onChange={(e) => setQText(e.target.value)}
                placeholder={t('teams.searchPlaceholder')}
                aria-label={t('teams.searchPlaceholder')}
                className="w-full bg-[#f7faf5] rounded-lg border border-transparent pl-10 pr-14 py-2 text-[#0d1f14] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#b5f03d] focus:border-[#b5f03d]/40 sm:text-sm"
              />
              <kbd
                aria-hidden
                className="hidden sm:inline-flex absolute right-3 top-1/2 -translate-y-1/2 items-center rounded border border-zinc-200 bg-zinc-100 px-1.5 py-0.5 text-[10px] font-mono text-zinc-500"
                title="Press / to focus search"
              >
                /
              </kbd>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto px-2">
               <label htmlFor="teams-sort" className="text-xs font-medium text-zinc-500 whitespace-nowrap hidden sm:inline">{t('teams.sortBy')}:</label>
               <select
                 id="teams-sort"
                 value={sortBy}
                 onChange={(e) => setSortBy(e.target.value)}
                 aria-label={t('teams.sortBy')}
                 className="border border-zinc-200 py-1.5 pl-3 pr-8 text-sm font-medium text-zinc-700 focus:outline-none focus:ring-2 focus:ring-[#b5f03d] focus:border-[#b5f03d]/40 cursor-pointer rounded-lg bg-white"
               >
                 <option value="updated">{t('teams.sortRecent')}</option>
                 <option value="alpha">{t('teams.sortAlpha')}</option>
               </select>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div
            className="grid gap-6 justify-start"
            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 380px))' }}
            role="status"
            aria-live="polite"
            aria-label="Loading teams"
          >
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="rounded-2xl bg-white border border-zinc-200 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] animate-pulse overflow-hidden"
              >
                <div className="h-1.5 w-full bg-[#b5f03d]/30" />
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    <Skeleton className="h-14 w-14 rounded-xl" />
                    <div className="flex-1 min-w-0 space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    <Skeleton className="h-3 w-2/3" />
                    <div className="flex gap-2 pt-1">
                      <Skeleton className="h-6 w-16 rounded-md" />
                      <Skeleton className="h-6 w-20 rounded-md" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-3 divide-x divide-zinc-200 border-t border-zinc-200 bg-[#f7faf5]">
                  <div className="py-3 flex items-center justify-center"><Skeleton className="h-3 w-12" /></div>
                  <div className="py-3 flex items-center justify-center"><Skeleton className="h-3 w-12" /></div>
                  <div className="py-3 flex items-center justify-center"><Skeleton className="h-3 w-16" /></div>
                </div>
              </div>
            ))}
            <span className="sr-only">Loading your teams…</span>
          </div>
        ) : totalTeams === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-3xl border border-zinc-200 border-dashed">
            <div className="h-16 w-16 bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 rounded-2xl flex items-center justify-center mb-4">
              <TrophyIcon className="h-8 w-8 text-[#5a8a1f]" />
            </div>
            <h3
              className="text-2xl text-[#0d1f14] uppercase"
              style={{ fontFamily: HEAD_FONT, fontWeight: 800, letterSpacing: '0.4px' }}
            >
              {t('teams.empty.title')}
            </h3>
            <p className="mt-2 max-w-md mx-auto mb-6 text-sm text-zinc-600">
              {t('teams.empty.description')}
            </p>
            <PrimaryButton as={RouterLink} to="/teams/create">
              {t('teams.empty.cta')}
            </PrimaryButton>
          </div>
        ) : (
          <div className="space-y-12">
            {groupedTeams.owned.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-4 px-1">
                   <SectionLabel>{t('teams.ownedTeams')}</SectionLabel>
                   <span className="bg-[#b5f03d]/15 text-[#3d6010] ring-1 ring-[#b5f03d]/40 px-2 py-0.5 rounded-full text-[10px] font-bold">{groupedTeams.owned.length}</span>
                </div>
                {renderList(groupedTeams.owned)}
              </section>
            )}

            {groupedTeams.managed.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-4 px-1">
                   <SectionLabel>{t('teams.managedTeams')}</SectionLabel>
                   <span className="bg-[#b5f03d]/15 text-[#3d6010] ring-1 ring-[#b5f03d]/40 px-2 py-0.5 rounded-full text-[10px] font-bold">{groupedTeams.managed.length}</span>
                </div>
                {renderList(groupedTeams.managed)}
              </section>
            )}
          </div>
        )}

        {/* Modals */}
        <InviteOptionsModal
          team={inviteOptionsFor}
          onClose={() => setInviteOptionsFor(null)}
          onAddPlayers={(t)     => createRosterLink(t, 'player')}
          onInviteParents={(t)  => createRosterLink(t, 'parent')}
          onInviteStaffEmail={(t) => openSendInvite(t, { channel: 'email' })}
          onCopyStaffLink={quickInviteLink}
          onShareViaSMS={handleShareViaSMS}
        />

        <SendInviteModal
          open={!!sendInviteFor}
          onClose={() => { setSendInviteFor(null); setSendChannel(null); }}
          team={sendInviteFor}
          channel={sendChannel}
        />

        {successModalData && (
          <InviteSuccessModal
            open={successModalData.open}
            url={successModalData.url}
            onClose={() => setSuccessModalData({ ...successModalData, open: false })}
          />
        )}

        {/* Labeled invite link modal (replaces window.prompt) */}
        <Modal
          open={!!labelModal}
          onClose={() => setLabelModal(null)}
          title={labelModal?.team?.name ? t('teams.labelModal.titleFor', { name: labelModal.team.name }) : t('teams.labelModal.title')}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              createLabeledInviteLink();
            }}
            className="space-y-4"
          >
            <div>
              <label htmlFor="invite-label" className="text-xs font-semibold text-[#0d1f14]">
                {t('teams.labelModal.question')}
              </label>
              <input
                id="invite-label"
                type="text"
                autoFocus
                value={labelModal?.label || ''}
                onChange={(e) => setLabelModal((s) => (s ? { ...s, label: e.target.value } : s))}
                placeholder={t('teams.labelModal.placeholder')}
                maxLength={80}
                className="mt-1 w-full bg-white rounded-lg border border-zinc-300 px-3 py-2 text-sm text-[#0d1f14] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#b5f03d] focus:border-[#b5f03d]/40"
              />
              <p className="mt-1.5 text-xs text-zinc-500">
                {t('teams.labelModal.helper')}
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <GhostButton type="button" onClick={() => setLabelModal(null)}>{t('teams.cancel')}</GhostButton>
              <PrimaryButton
                type="submit"
                disabled={!labelModal?.label?.trim()}
              >
                {t('teams.labelModal.create')}
              </PrimaryButton>
            </div>
          </form>
        </Modal>

        {/* Leave team confirmation (replaces window.confirm) */}
        <Modal
          open={!!leaveModal}
          onClose={() => setLeaveModal(null)}
          title={t('teams.leaveModal.titleFor', { name: leaveModal?.name || 'team' })}
        >
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">
              {t('teams.leaveModal.body')}
            </p>
            <div className="flex justify-end gap-3">
              <GhostButton onClick={() => setLeaveModal(null)}>{t('teams.cancel')}</GhostButton>
              <DangerButton onClick={confirmLeaveTeam}>{t('teams.leaveModal.confirm')}</DangerButton>
            </div>
          </div>
        </Modal>

        <Modal open={!!teamToArchive} onClose={() => setTeamToArchive(null)} title={t('teams.archiveModal.titleFor', { name: teamToArchive?.name || 'Team' })}>
           <div className="space-y-4">
              <p className="text-sm text-zinc-600">
                {t('teams.archiveModal.body')}
              </p>

              {usageLoading && <div className="text-xs text-zinc-500 flex items-center gap-2"><ArrowPathIcon className="h-3 w-3 animate-spin"/> {t('teams.archiveModal.checking')}</div>}

              {usage && (usage.scrimmagesOpen > 0 || usage.scrimmagesConfirmed > 0) && (
                <div className="bg-rose-50 text-rose-700 p-3 rounded-lg text-sm border border-rose-200">
                  <strong>{t('teams.archiveModal.cannotArchiveLabel')}</strong> {t('teams.archiveModal.cannotArchiveDetail', { confirmed: usage.scrimmagesConfirmed, open: usage.scrimmagesOpen })}
                </div>
              )}

              <div className="pt-2">
                 <label className="text-xs font-semibold text-[#0d1f14]">{t('teams.archiveModal.reasonLabel')}</label>
                 <textarea
                   value={reason}
                   onChange={e => setReason(e.target.value)}
                   placeholder={t('teams.archiveModal.reasonPlaceholder')}
                   rows={3}
                   className="mt-1 w-full bg-white rounded-lg border border-zinc-300 px-3 py-2 text-sm text-[#0d1f14] placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#b5f03d] focus:border-[#b5f03d]/40"
                 />
              </div>

              <div className="flex justify-end gap-3 mt-4">
                 <GhostButton onClick={() => setTeamToArchive(null)}>{t('teams.cancel')}</GhostButton>
                 <DangerButton onClick={confirmArchive} disabled={usageLoading || (usage?.scrimmagesOpen > 0 || usage?.scrimmagesConfirmed > 0)}>
                   {t('teams.archiveModal.confirm')}
                 </DangerButton>
              </div>
           </div>
        </Modal>

      </div>
    </div>
  );
}